import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  Auth,
  signInWithEmailAndPassword,
  User,
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "@angular/fire/auth";
import { FirebaseApp } from "@angular/fire/app";
import {
  getFirestore,
  collection,
  getDoc,
  setDoc,
  addDoc,
  doc,
  query,
  getDocs,
  where,
} from "@angular/fire/firestore";
import { getFunctions, httpsCallable } from "@angular/fire/functions";
import { BehaviorSubject } from "rxjs";
import { environment } from "src/environments/environment";
import { AlertModalService } from "./alert-modal.service";
import { User as UserProfile } from "src/app/shared/model/user.model";

/**
 * Service to connect and vlidate authorizations (Login/Register/Updates)
 */
@Injectable({
  providedIn: "root",
})
export class AuthService {
  /** Instance for the current logged user's company as observable */
  public authUser = new BehaviorSubject<User | null>(null);
  authUserChanges$ = this.authUser.asObservable();
  unsubscribeAuthState: any; // Function to unsuscribe from auth changes

  /** User profile object as observable */
  protected userProfile = new BehaviorSubject<UserProfile | null>(null);
  userProfileChanges$ = this.userProfile.asObservable();
  unsubscribeUserProfileState: any; // Function to unsuscribe from user profile changes

  /** Contains the firestore Database reference. */
  public firebaseDB: any;

  /** Contains the firestore Functions reference. */
  public firebaseFunctions: any;

  /** Cloud function to save New User data */
  public cloudFn_addUser: any;

  /** Cloud function to update User data */
  public cloudFn_updateUser: any;

  /**
   * Constructor for the auth service
   * @param {Auth} auth The auth object to connect with FB.
   * @param {Router} router Router used to redirect the user if necesary.
   * */
  constructor(
    private auth: Auth,
    private router: Router,
    private alertModalService: AlertModalService,
    private firebaseApp: FirebaseApp
  ) {
    // It´s call when the state of the auth changes. It updates the authUSer on the service.
    this.unsubscribeAuthState = onAuthStateChanged(this.auth, (user) => {
      if (user) {
        // Updates the user instance on changes
        this.authUser.next(user);

        // Updates the profile user with the uid of the authUser
        this.getUserByUid(user.uid).then((userProfile) => {
          if (userProfile) this.userProfile.next(userProfile);
        });
      } else {
        // When no user 'clears' user data and redirects to login page
        this.authUser.next(null);
        this.router.navigate(["authentication/login"]);
      }
    });

    //Sets The firestore DB
    this.firebaseDB = getFirestore(this.firebaseApp, environment.dataBaseId);

    //Sets The firesbase functions reference
    this.firebaseFunctions = getFunctions(this.firebaseApp);

    //Sets the function to save user through cloud functions
    this.cloudFn_addUser = httpsCallable(this.firebaseFunctions, "addUser");

    //Sets the function to update user through cloud functions
    this.cloudFn_updateUser = httpsCallable(
      this.firebaseFunctions,
      "updateUser"
    );
  }

  /**
   * Allows the user to login using email and password.
   * @param {string} email The user email.
   * @param {string} password The user password.
   * @returns
   */
  async _signInWithEmailAndPassword(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password)
      .then((result) => {
        this.getUserByUid(result.user.uid);
        // When user logs through email and Password
        this.router.navigate(["learning/learninglist"]);
      })
      .catch((error) => {
        // In case of error while Signing in with email and Password
      });
  }

  /**
   * Function to sing out a user from the auth service
   */
  _signOut() {
    signOut(this.auth)
      .then(() => {
        // Redirects on sign out
        this.router.navigate(["athentication/login"]);
      })
      .catch((error) => {
        // In case of error while Signing out
      });
  }

  /**
   * Async call to check if user is signedIn on the auth service
   */
  async userIsLoggedIn(): Promise<boolean> {
    var userIsLoggeIn = false;

    // Call the auth async service and wait for the response
    await this.auth.authStateReady().then(() => {
      if (this.auth.currentUser) {
        console.log("Current User", this.auth.currentUser);
        userIsLoggeIn = true;
      }
    });

    // Return signedIn status
    return userIsLoggeIn;
  }

  /**
   * Async call to check if user has email verified
   */
  async userHasEmailVerified(): Promise<boolean> {
    var userHasEmailValidated = false;

    // Call the auth async service and wait for the response
    await this.auth.authStateReady().then(() => {
      if (this.auth.currentUser) {
        userHasEmailValidated = this.auth.currentUser.emailVerified;
      }
    });

    // Return signedIn status
    return userHasEmailValidated;
  }

  /**
   * Get the user profile picture
   * Every provider of the user will be looked up, the first picture found will be
   * returned. In case no provider has a picture a defualt profile picture for the app will be returned
   * @returns The url for the profile picture of the current logged user.
   */
  getUserProfilePicture(): string {
    // Set the Default picture value
    var profilePicture = environment.defaultProfilePicture;

    // Checks if user is loaded and in that case go trhough all the providers looking for the first picture
    if (this.authUser.value) {
      var exit = false; // Flag to tell forEach iteration to pick the first photo
      this.authUser.value.providerData.forEach((provider) => {
        // Checks if the current provider has a profilePicture
        if (provider.photoURL && !exit) {
          // Sets the profile picture right from the user proviedr
          profilePicture = provider.photoURL;
          exit = true;
        }
      });
    }

    // Return the profile picture
    return profilePicture;
  }

  /**
   * Creates a new user given the information
   * @param email The user's email
   * @param password The user's password
   * @param firstName The First name of the user
   * @param lastName The Last name of the user
   */
  async registerNewUser(email, password, firstName, lastName) {
    var uid: string | null = null;
    await createUserWithEmailAndPassword(this.auth, email, password).then(
      (userCredentials) => {
        uid = userCredentials.user.uid;
        // Sends a verification email
        sendEmailVerification(userCredentials.user).then(() => {
          // Show message informing user about the verification email
          this.alertModalService.show(
            "Verification email sent",
            "A verification email was sent to: " + userCredentials.user.email
          );
        });
        this.router.navigate(["dashboard/default"]);
      }
    );

    // Check if user auth was registered by uid
    if (uid) {
      // Updates the current user profile with the given information.
      var user = new UserProfile(firstName, lastName, email, uid);
      this.userProfile.next(user);
      await this.saveUser(this.userProfile.value!);
    }
  }

  /**
   * Function to sen a verification email to the current user
   * @returns true if the email has been sent, false if not
   */
  async _sendEmailVerification(): Promise<boolean> {
    var emailSent: boolean = false;
    if (this.authUser.value) {
      await sendEmailVerification(this.authUser.value).then(() => {
        emailSent = true;
      });
    }
    return emailSent;
  }

  /**
   * Sends a recovery password email to the given email
   * @param email The email to check and send the recovery password message
   * @returns Promise<boolean> tho know if the email has been sent or not.
   */
  async _sendPasswordResetEmail(email: string): Promise<boolean> {
    var recoveryEmailSent = false;
    await sendPasswordResetEmail(this.auth, email)
      .then(() => {
        recoveryEmailSent = true;
      })
      .catch(() => {
        recoveryEmailSent = false;
      });
    return recoveryEmailSent;
  }

  /**
   * Saves the user profile.
   * If new user is input (New register user) the document is created.
   * If is an unpdate, the update will happen.
   * This method uses Cloud functions to create or update users on Firestore
   * @param user The given user profile to be save.
   */
  async saveUser(user: UserProfile) {
    // Checks if is a new user (No docId)
    if (!user.docId) {
      // Is a new User
      this.cloudFn_addUser(user).then((result: any) => {
        user.docId = result.docRef;
      });
    } else {
      // Update user
      this.cloudFn_updateUser(user).then((result: any) => {
        // User updated
      });
    }
  }

  /**
   * Retrieves the user profile information
   * @param uid The UID of the user
   * @returns Promise <UserProfile|null>, null if no user is found or the userProfile object if this is found.
   */
  async getUserByUid(uid: string): Promise<UserProfile | null> {
    const filteredCollectionQuery = query(
      collection(this.firebaseDB, "users"),
      where("uid", "==", uid)
    );
    const querySnapshot = await getDocs(filteredCollectionQuery).catch(() => {
      this.userProfile.next(null);
    });
    if (querySnapshot) {
      if (querySnapshot.empty) {
        this.userProfile.next(null);
        return null;
      } else {
        this.userProfile.next(querySnapshot.docs[0].data() as UserProfile);
        return querySnapshot.docs[0].data() as UserProfile;
      }
    } else {
      return null;
    }
  }
}
